import { Modal, Stack, spacing, Text } from '@scality/core-ui';
import { Box, Button } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { useInstanceLabel } from '../../../hooks/portal/useInstanceLabel';
import { useInstanceOptions } from '../../../hooks/portal/useInstanceOptions';
import { CUSTOMER_SUPPORT_AGENT_ID, useAgentItems } from '../../../hooks/useAgentItems';
import { useChat } from '../../../hooks/useChat';
import { useChatUrlInstanceIds } from '../../../hooks/useChatUrlInstanceIds';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useMcpInstanceId } from '../../../hooks/useMcpInstanceId';
import { useRestoreSelectedAgent } from '../../../hooks/useRestoreSelectedAgent';
import { useSelectAgentAnimation } from '../../../hooks/useSelectAgentAnimation';
import { useMcpBridge } from '../../../mcp/McpBridgeProvider';
import { PORTAL_SEARCH_DEBOUNCE_MS } from '../../../portal/portalClient';
import { GuardianContext } from '../../GuardianProvider';
import type { GuardianAgent } from './selectors';
import { AgentSelector, DeploymentSelector } from './selectors';

const ChatContextualizerBox = styled.div`
  display: flex;
  color: white;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  position: relative;
  margin-top: -${spacing.r8};
  padding-right: ${spacing.r24};
  padding-bottom: ${spacing.r8};
`;

type ChatContextualizerProps = {
  chatInputRef: React.RefObject<HTMLTextAreaElement | null>;
  /** When true, instance dropdown opens below the trigger (e.g. welcome). When false, open above (chat composer at bottom). */
  menuOpenBelow?: boolean;
};

const ConfirmAgentModalFooter: React.FC<{
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ onCancel, onConfirm }) => (
  <Box display="flex" justifyContent="end" gap={spacing.r8}>
    <Button variant="outline" onClick={onCancel} label="Cancel" />
    <Button variant="primary" onClick={onConfirm} label="Confirm" />
  </Box>
);

export const ChatContextualizer: React.FC<ChatContextualizerProps> = ({ chatInputRef, menuOpenBelow = false }) => {
  const { selectedInstanceIds, setSelectedInstanceIds } = useChatUrlInstanceIds();

  const [instanceSearch, setInstanceSearch] = useState('');
  const debouncedInstanceSearch = useDebouncedValue(instanceSearch, PORTAL_SEARCH_DEBOUNCE_MS);
  const {
    items: deploymentItems,
    totalCount,
    status: instancesStatus,
    isServerFiltered,
    isSearching,
  } = useInstanceOptions(debouncedInstanceSearch);

  const { resolvedId: mcpResolvedInstanceId, isResolving: isResolvingMcpInstanceId } =
    useMcpInstanceId(setSelectedInstanceIds);
  const { mcpInstanceId } = useMcpBridge();

  // Hide the selector while an MCP-supplied id is still being translated: showing it and
  // then removing it reads as a glitch. A failed resolution still leaves a usable selector.
  const isMcpInstanceIdSet = !!mcpResolvedInstanceId || (!!mcpInstanceId && isResolvingMcpInstanceId);

  const selectAgentWithAnimation = useSelectAgentAnimation();
  const { selectedAgent, setSelectedAgent, setConversationId } = useContext(GuardianContext);
  const { conversation } = useChat();
  const { agentItems, status: agentsStatus } = useAgentItems();
  const restoreSelectedAgent = useRestoreSelectedAgent();

  const hasConversationMessages = !conversation.isNew;
  const [isConfirmAgentModalOpen, setIsConfirmAgentModalOpen] = useState(false);
  const [pendingAgent, setPendingAgent] = useState<GuardianAgent | null>(null);

  useEffect(() => {
    restoreSelectedAgent(agentItems);
  }, [restoreSelectedAgent, agentItems]);

  const pinnedAgent = agentItems.find((agent) => agent.isPinned);

  // The picker is hidden under a pin, so the pin overrides any restored or missing selection.
  useEffect(() => {
    if (pinnedAgent) {
      if (selectedAgent?.id !== pinnedAgent.id) setSelectedAgent(pinnedAgent);
      return;
    }
    if (selectedAgent || hasConversationMessages) return;
    if (agentItems.length === 1 && agentItems[0].id === CUSTOMER_SUPPORT_AGENT_ID && agentItems[0].isSelectable) {
      setSelectedAgent(agentItems[0]);
    }
  }, [agentItems, pinnedAgent, selectedAgent, hasConversationMessages, setSelectedAgent]);

  const handleAgentSelect = useCallback(
    (agent: GuardianAgent | null) => {
      if (agent === null) {
        setConversationId(uuidv4());
        return;
      }
      if (!hasConversationMessages) {
        selectAgentWithAnimation(agent);
        return;
      }
      setPendingAgent(agent);
      setIsConfirmAgentModalOpen(true);
    },
    [setSelectedAgent, setConversationId, hasConversationMessages, selectAgentWithAnimation],
  );

  const closeConfirmAgentModal = useCallback(() => {
    setIsConfirmAgentModalOpen(false);
    setPendingAgent(null);
  }, []);

  const confirmStartNewConversation = useCallback(() => {
    if (pendingAgent) {
      setConversationId(uuidv4());
      setSelectedAgent(pendingAgent);
    }
    closeConfirmAgentModal();
  }, [pendingAgent, setConversationId, setSelectedAgent, closeConfirmAgentModal]);

  const selectedInstanceId = selectedInstanceIds[0] ?? '';
  const { label: selectedInstanceLabel } = useInstanceLabel(selectedInstanceId);
  const selectedDeploymentItem = selectedInstanceId ? { id: selectedInstanceId, label: selectedInstanceLabel } : null;
  const handleInstanceMenuClose = useCallback(() => {
    chatInputRef.current?.focus();
  }, [chatInputRef]);

  return (
    <ChatContextualizerBox>
      {!pinnedAgent && (
        <AgentSelector
          items={agentItems}
          selectedAgent={selectedAgent}
          onAgentSelect={handleAgentSelect}
          status={agentsStatus}
        />
      )}
      {!isMcpInstanceIdSet && (
        <DeploymentSelector
          items={deploymentItems}
          selectedItems={selectedDeploymentItem ? [selectedDeploymentItem] : []}
          onSelectionChange={(items) => setSelectedInstanceIds(items.map((item) => item.id))}
          menuOpenBelow={menuOpenBelow}
          status={instancesStatus}
          onMenuClose={handleInstanceMenuClose}
          onSearchChange={isServerFiltered ? setInstanceSearch : undefined}
          // Also true during the 250 ms debounce, before any request exists — otherwise the
          // spinner appears a beat after the user types, which is when they most expect it.
          isSearching={isServerFiltered && (isSearching || instanceSearch !== debouncedInstanceSearch)}
          totalCount={totalCount}
        />
      )}

      <Modal
        isOpen={isConfirmAgentModalOpen}
        title="Start a new conversation?"
        footer={<ConfirmAgentModalFooter onCancel={closeConfirmAgentModal} onConfirm={confirmStartNewConversation} />}
      >
        <Stack direction="vertical" gap="r8">
          <Text color="textSecondary">Selecting this agent will start a new conversation.</Text>
          <Text color="textSecondary">You can go back to this conversation at any moment.</Text>
        </Stack>
      </Modal>
    </ChatContextualizerBox>
  );
};
